'use client'
import { isEmpty } from 'lodash'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { FormProvider, SubmitHandler } from 'react-hook-form'

import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import PhoneNumberInputController from '@/components/ui/forms/PhoneNumberInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'

import { useBooking } from '../../model/bookingContext'
import { PatientInfoFormData, usePatientInfoForm } from '../../model/patientInfoForm'
import { useOrderManagement } from '../../model/useOrderManagement'
import { BookingStepEnum } from '../../model/bookingTypes'

type PatientInfoFormProps = {
  initialValues?: PatientInfoFormData
  meetingLinkSlug: string
}

export function PatientInfoForm(props: PatientInfoFormProps) {
  const { initialValues, meetingLinkSlug } = props
  const { state, dispatch, isLastActionableStep } = useBooking()

  // Get values from context
  const meetingPlace = state.meetingPlace
  const meetingPlaceDescription = state.meetingPlaceDescription
  const speciality = state.selectedSpeciality

  const rhForm = usePatientInfoForm(initialValues)
  const {
    control,
    formState: { isValid },
    handleSubmit,
    setValue,
  } = rhForm

  // Use the orderManagement hook for centralized order management
  const { draftOrder, isDraftingOrder, isLoadingMeetingLink } = useOrderManagement(meetingLinkSlug)

  const onSubmit: SubmitHandler<PatientInfoFormData> = async (data) => {
    // Clear any previous errors
    if (state.errors.order) {
      dispatch({ type: 'CLEAR_ERROR', payload: 'order' })
    }

    const patientInfo = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: !isEmpty(data.phone) ? data.phone : undefined,
    }

    // Store patient info in context
    dispatch({ type: 'SET_PATIENT_INFO', payload: patientInfo, goToNextStep: !!state.orderId })

    if (state.orderId) return

    // Determine if this is the last actionable step using the context function
    const isLastStep = isLastActionableStep(BookingStepEnum.PATIENT_INFORMATION)

    try {
      // Create a draft or pending order using the explicit method
      const draftedOrderId = await draftOrder(patientInfo, isLastStep)
      if (!draftedOrderId) {
        dispatch({
          type: 'SET_ERROR',
          payload: { field: 'order', message: 'Failed to create order' },
        })
        return
      }

      // Store order ID in context
      dispatch({
        type: 'SET_ORDER_ID',
        payload: draftedOrderId,
        goToNextStep: true,
      })
    } catch (error) {
      console.error('Error creating order:', error)
      dispatch({
        type: 'SET_ERROR',
        payload: { field: 'order', message: 'Failed to create order' },
      })
    }
  }

  const isSubmitButtonDisabled = isDraftingOrder || isLoadingMeetingLink || !isValid || !meetingPlace || !meetingPlaceDescription || !speciality

  return (
    <FormProvider {...rhForm}>
      <form className="flex flex-col space-y-4" onSubmit={handleSubmit(onSubmit)}>
        {state.errors.order && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-600">{state.errors.order}</div>}
        <div className="bg-white shadow-sm sm:shadow-none rounded-lg">
          <div className="px-4 py-6 sm:p-0">
            <div className="grid grid-rows-auto sm:grid-cols-2 gap-4">
              <div className="h-fit">
                <TextInputController control={control} fullWidth label={t`First name`} name="firstName" required />
              </div>
              <div className="h-fit">
                <TextInputController control={control} fullWidth label={t`Last name`} name="lastName" required />
              </div>
              <div className="h-fit">
                <TextInputController control={control} fullWidth label={t`Email`} name="email" required />
              </div>
              <div className="h-fit">
                <PhoneNumberInputController
                  control={control}
                  label={t`Phone`}
                  name="phone"
                  onCountryChange={(countryCode) => {
                    setValue('countryCode', countryCode)
                  }}
                />
              </div>
              <div className="flex flex-col space-y-2 sm:col-span-2">
                <CheckboxInputController
                  classes={{ label: 'font-light text-gray-500' }}
                  control={control}
                  label={t`I have my Carte Vitale available *`}
                  name="isCarteVitaleAvailable"
                />
                <CheckboxInputController
                  classes={{ label: 'font-light text-gray-500' }}
                  control={control}
                  label={t`I accept the general conditions of use *`}
                  name="isGCUAccepted"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end sm:absolute sm:top-full sm:right-0 sm:!m-0">
          <button
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-300 sm:rounded-none"
            disabled={isSubmitButtonDisabled}
            data-testid="patient-info-confirm"
            type="submit"
          >
            {(isDraftingOrder || isLoadingMeetingLink) && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
            )}
            <Trans>Submit</Trans>
          </button>
        </div>
      </form>
    </FormProvider>
  )
}
